package org.example.zone360.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.stevdza.san.kotlinbs.components.initializeTooltips
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.alignItems
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.example.zone360.theme.Palette
import org.example.zone360.theme.toSitePalette
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

@Composable
fun WidgetSelectionPopup(
    selectedIds: Set<Int>,
    onSave: () -> Unit,
    onClose: () -> Unit,
    isLoading: Boolean,
    content: @Composable () -> Unit
) {
    Box(
        modifier = Modifier
            .styleModifier {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                property("right", "0")
                property("bottom", "0")
                property("width", "100vw")
                property("height", "100vh")
                property("background-color", "rgba(0,0,0,0.7)")
                property("z-index", "99999")
                property("overflow", "auto")  // ✅ Backdrop scrolls
                property("display", "flex")
                property("justify-content", "center")
                property("padding", "10px")
            }
            .onClick { onClose() }
    ) {
        // ✅ Modal - not fixed, scrolls with backdrop
        Box(
            modifier = Modifier
                .styleModifier {
                    property("max-width", "1490px")
                    property("width", "100%")
                    property("margin", "auto")
                    property("margin-top", "auto")
                    property("margin-bottom", "auto")
                }
                .backgroundColor(ColorMode.current.toSitePalette().modalBackgroundColor.rgb)
                .borderRadius(16.px)
                .padding(15.px)
                .onClick { it.stopPropagation() }
        ) {
            Column {
                Row(
                    modifier = Modifier.fillMaxWidth().margin(bottom = 16.px),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        "Select Widgets (${selectedIds.size}/8)",
                        Modifier.fontSize(18.px).fontWeight(700)
                    )
                    Box(
                        Modifier.cursor(Cursor.Pointer).onClick { onClose() }
                    ) {
                        SpanText("✕")
                    }
                }

                content()

                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 20.px)
                        .margin(top = 20.px, bottom = 10.px),
                    horizontalArrangement = Arrangement.End
                ) {
                    CommonButton(
                        text = "Save",
                        width = 100.px,
                        isLoading = isLoading,
                        onCLick = { onSave() }
                    )
                }
            }
        }
    }
}

@Composable
fun WidgetSelectionValidationPopup(
    currentCount: Int,
    onChooseMore: () -> Unit,
    onKeepFour: () -> Unit
) {
    val sitePalette = ColorMode.current.toSitePalette()
    val moreNeeded = 8 - currentCount

    // Determine values based on count using separate variables
    val title: String
    val subtitle: String
    val buttonText: String

    when (currentCount) {
        in 1..3 -> {
            title = "Please select at least 4 widgets"
            subtitle = "You've selected $currentCount widgets. Minimum required is 4."
            buttonText = "Add More Widgets"
        }
        in 5..7 -> {
            title = "Select at least 8 or keep 4"
            subtitle = "Widgets must total 4 or 8 so rows stay evenly aligned."
            buttonText = "Choose $moreNeeded more"
        }
        in 9..10 -> {
            title = "Maximum 8 widgets allowed"
            subtitle = "You've selected $currentCount widgets. Maximum allowed is 8."
            buttonText = "Close"
        }
        else -> {
            title = "Invalid selection"
            subtitle = "Please select 4 or 8 widgets."
            buttonText = "Add More Widgets"  // ✅ Show "Add More" button for invalid selection
        }
    }

    Box(
        modifier = Modifier
            .styleModifier {
                property("position", "fixed")
                property("top", "0"); property("left", "0")
                property("right", "0"); property("bottom", "0")
                property("z-index", "999999")
                property("background-color", "rgba(0,0,0,0.7)")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
            }
    ) {
        Box(
            modifier = Modifier
                .backgroundColor(sitePalette.modalBackgroundColor.rgb)
                .borderRadius(14.px)
                .padding(24.px)
                .styleModifier { property("width", "90%"); property("max-width", "340px") }
        ) {
            Column(horizontalAlignment = Alignment.CenterHorizontally) {
                // Title with error color
                SpanText(
                    title,
                    Modifier
                        .fontSize(15.px)
                        .fontWeight(600)
                        .color(if (currentCount in 9..10) Colors.Red else Color.rgb(220, 38, 38))
                        .margin(bottom = 6.px)
                )

                // Subtitle
                SpanText(
                    subtitle,
                    Modifier
                        .fontSize(13.px)
                        .color(Color.rgb(107, 114, 128))
                        .margin(bottom = 18.px)
                )

                // Buttons based on the error type
                when (currentCount) {
                    in 1..3 -> {
                        // Only "Add More Widgets" button
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(40.px)
                                .borderRadius(8.px)
                                .backgroundColor(Palette.CoachTalkPrimaryBlue.rgbLight)
                                .cursor(Cursor.Pointer)
                                .onClick { onChooseMore() },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                buttonText,
                                Modifier.fontSize(13.px).fontWeight(600).color(Colors.White)
                            )
                        }
                    }
                    in 5..7 -> {
                        // Two buttons: Keep 4 and Choose More
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.SpaceBetween
                        ) {
                            Box(
                                modifier = Modifier
                                    .width(48.percent)
                                    .height(40.px)
                                    .borderRadius(8.px)
                                    .border(1.px, LineStyle.Solid, Color.rgb(229, 231, 235))
                                    .cursor(Cursor.Pointer)
                                    .onClick { onKeepFour() },
                                contentAlignment = Alignment.Center
                            ) {
                                SpanText("Keep First 4", Modifier.fontSize(13.px).fontWeight(600))
                            }
                            Box(
                                modifier = Modifier
                                    .width(48.percent)
                                    .height(40.px)
                                    .borderRadius(8.px)
                                    .backgroundColor(Palette.CoachTalkPrimaryBlue.rgbLight)
                                    .cursor(Cursor.Pointer)
                                    .onClick { onChooseMore() },
                                contentAlignment = Alignment.Center
                            ) {
                                SpanText(
                                    buttonText,
                                    Modifier.fontSize(13.px).fontWeight(600).color(Colors.White)
                                )
                            }
                        }
                    }
                    in 9..10 -> {
                        // Only "Close" button
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(40.px)
                                .borderRadius(8.px)
                                .backgroundColor(Color.rgb(239, 68, 68))
                                .cursor(Cursor.Pointer)
                                .onClick { onChooseMore() },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                buttonText,
                                Modifier.fontSize(13.px).fontWeight(600).color(Colors.White)
                            )
                        }
                    }
                    else -> {
                        // ✅ "Invalid selection" case - Show "Add More Widgets" button
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(40.px)
                                .borderRadius(8.px)
                                .alignItems(AlignItems.Center)
                                .backgroundColor(Palette.CoachTalkPrimaryBlue.rgbLight)
                                .cursor(Cursor.Pointer)
                                .onClick { onChooseMore() },
                            contentAlignment = Alignment.Center
                        ) {
                            SpanText(
                                buttonText,
                                Modifier.fontSize(13.px).fontWeight(600).color(Colors.White)
                            )
                        }
                    }
                }
            }
        }
    }
}